import { DateTime } from 'luxon';

/**
 * Rangos de fechas predefinidos para los filtros de las listas.
 *
 * Todo en texto `yyyy-MM-dd` (el mismo formato que viaja al backend). Las
 * funciones reciben `today` para poder probarlas con una fecha fija; en la app
 * se usa el día de hoy.
 */

const FORMAT = 'yyyy-MM-dd';
const NATURAL = 'dd/MM/yyyy';

export const ALL_DATES_LABEL = 'Todas las fechas';

export const PRESET_KEYS = [
    { key: 'today', label: 'Hoy' },
    { key: 'yesterday', label: 'Ayer' },
    { key: 'last7', label: 'Últimos 7 días' },
    // Opcional: solo sale si la vista lo pide (`extraPresets` de DateRangeField); las listas no lo muestran.
    { key: 'week', label: 'Esta semana', optional: true },
    { key: 'month', label: 'Este mes' },
    { key: 'lastMonth', label: 'Mes pasado' }
];

const fmt = (date) => date.toFormat(FORMAT);

/** `{ start, end }` de un preset. "Este mes" llega hasta hoy: aún no hay ventas del futuro. */
export function presetRange(key, today = DateTime.now()) {
    switch (key) {
        case 'today':
            return { start: fmt(today), end: fmt(today) };
        case 'yesterday': {
            const day = today.minus({ days: 1 });
            return { start: fmt(day), end: fmt(day) };
        }
        case 'last7':
            return { start: fmt(today.minus({ days: 6 })), end: fmt(today) };
        case 'week':
            // Luxon (ISO) empieza la semana en lunes: de lunes a domingo completos.
            return { start: fmt(today.startOf('week')), end: fmt(today.endOf('week')) };
        case 'month':
            return { start: fmt(today.startOf('month')), end: fmt(today) };
        case 'lastMonth': {
            const previous = today.minus({ months: 1 });
            return { start: fmt(previous.startOf('month')), end: fmt(previous.endOf('month')) };
        }
        default:
            return { start: '', end: '' };
    }
}

/**
 * Presets con su rango ya calculado, en el orden en que se muestran. Los opcionales (`optional`) solo entran si su
 * clave viene en `include`.
 */
export function presetList(today = DateTime.now(), include = []) {
    return PRESET_KEYS.filter((preset) => !preset.optional || include.includes(preset.key)).map((preset) => ({
        ...preset,
        ...presetRange(preset.key, today)
    }));
}

/** Clave del preset que coincide con el rango, o '' si es un rango libre. */
export function matchPreset(start, end, today = DateTime.now(), include = []) {
    if (!start && !end) return 'all';
    const found = presetList(today, include).find((preset) => preset.start === start && preset.end === end);
    return found ? found.key : '';
}

/** Ordena dos fechas (el calendario las da en el orden en que se tocaron). */
export function sortRange(first, second) {
    if (!first || !second) return { start: first || second || '', end: first || second || '' };
    return first <= second ? { start: first, end: second } : { start: second, end: first };
}

const natural = (date) => DateTime.fromFormat(date, FORMAT).toFormat(NATURAL);

/** Texto corto del rango para el campo y el chip: "Hoy", "Ayer", "01/10/2026 – 15/10/2026". */
export function describeRange(start, end, today = DateTime.now(), include = []) {
    const key = matchPreset(start, end, today, include);
    if (key === 'all') return ALL_DATES_LABEL;
    if (key) return PRESET_KEYS.find((preset) => preset.key === key).label;
    if (start && !end) return `Desde ${natural(start)}`;
    if (!start && end) return `Hasta ${natural(end)}`;
    if (start === end) return natural(start);
    return `${natural(start)} – ${natural(end)}`;
}
